import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toFinnishLabel } from "@/lib/initStepLabels";
import type { EditableTopicRow } from "@/lib/inspectionTopicRows";
import {
  outcomeStatusLabel,
  type InspectionTopicRewriteFlow,
} from "@/hooks/useInspectionTopicRewriteFlow";
import { AlertIcon, SpinnerIcon, SuccessIcon } from "@/lib/icons";

/** The DialogDescription text for the current step, for the host's own DialogHeader. */
export function inspectionTopicRewriteStepDescription(
  step: InspectionTopicRewriteFlow["step"],
): string {
  if (step === "scope") return "Valitse, mihin tarkastuksiin muutos kohdistuu.";
  if (step === "confirmBase") return "Vahvista projektipohjan korjaus.";
  return "";
}

/**
 * Body and footer for the scope / confirmBase / progress steps that follow a
 * tarkastuskohdat edit. Shared by the standalone tarkastuskohdat editor and
 * the tarkastuskohdat section embedded in Muokkaa tehtävän tietoja.
 */
export function InspectionTopicRewriteSteps({
  flow,
  taskId,
  rows,
  onBack,
  onClosed,
  onDismiss,
}: {
  flow: InspectionTopicRewriteFlow;
  taskId: string;
  rows: EditableTopicRow[];
  /** "Takaisin" from the scope step — returns to editing the rows. */
  onBack: () => void;
  /** A synchronous run genuinely updated something; the caller closes and refreshes. */
  onClosed: () => void;
  /** The progress step's "Sulje" button. */
  onDismiss: () => void;
}) {
  const {
    step,
    setStep,
    submitting,
    applyToWholeProject,
    setApplyToWholeProject,
    applyToBaseTemplate,
    setApplyToBaseTemplate,
    error,
    outcomes,
    templateFailures,
    displayProgress,
    finishedWithoutFrame,
    progressPercent,
    confirm,
  } = flow;

  const runConfirm = async () => {
    const result = await confirm(taskId, rows);
    if (result === "closed") onClosed();
  };

  if (step === "scope") {
    return (
      <>
        <div className="grid gap-4 py-4 px-1 overflow-y-auto overflow-x-hidden min-h-0 min-w-0">
          <div className="grid gap-2">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="inspection-topic-rewrite-scope"
                checked={!applyToWholeProject}
                onChange={() => setApplyToWholeProject(false)}
                disabled={submitting}
                className="mt-1 h-4 w-4 accent-primary"
              />
              <span>Vain tämä tarkastus</span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="inspection-topic-rewrite-scope"
                checked={applyToWholeProject}
                onChange={() => setApplyToWholeProject(true)}
                disabled={submitting}
                className="mt-1 h-4 w-4 accent-primary"
              />
              <span>Kaikki tämän pohjan tarkastukset hankkeessa</span>
            </label>
          </div>

          <div className="grid gap-2 pt-2 border-t">
            <label className="flex items-start gap-2 text-sm cursor-pointer pt-2">
              <Checkbox
                checked={applyToBaseTemplate}
                onCheckedChange={(value) => setApplyToBaseTemplate(!!value)}
                disabled={submitting}
                className="mt-0.5"
              />
              <span>Korjaa myös koko Tenconin laajuinen tarkastuspohja</span>
            </label>
            <p className="text-xs text-muted-foreground pl-6">
              Tämä kirjoittaa tarkastuskohdat uudelleen yrityksen laajuiseen
              tarkastuspohjaan. Jokainen hanke, joka alustetaan tämän jälkeen,
              saa pohjasta nämä tarkastuskohdat vanhojen tilalle. Jo alustetut
              hankkeet eivät muutu.
            </p>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {outcomes.length > 0 && (
            <div className="rounded-md border p-3 text-sm space-y-1">
              {outcomes.map((outcome) => (
                <p key={outcome.taskId}>
                  <span className="font-medium">
                    {outcomeStatusLabel(outcome.status)}:
                  </span>{" "}
                  {outcome.message || "Ei lisätietoa."}
                </p>
              ))}
            </div>
          )}

          {templateFailures.length > 0 && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700 space-y-1">
              <p className="font-medium">Projektipohjan päivitys epäonnistui:</p>
              {templateFailures.map((failure) => (
                <p key={failure.templateId}>
                  {failure.templateId}: {failure.message}
                </p>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onBack} disabled={submitting}>
            Takaisin
          </Button>
          <Button
            onClick={() => {
              if (applyToBaseTemplate) {
                setStep("confirmBase");
                return;
              }
              void runConfirm();
            }}
            disabled={submitting}
          >
            {submitting && <SpinnerIcon className="mr-2 h-4 w-4 animate-spin" />}
            Tallenna
          </Button>
        </DialogFooter>
      </>
    );
  }

  if (step === "confirmBase") {
    return (
      <>
        <div className="py-4 px-1 space-y-3 text-sm overflow-y-auto overflow-x-hidden min-h-0 min-w-0">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900 space-y-2">
            <p className="font-medium">Korjaat myös projektipohjan.</p>
            <p>
              Tarkastuspohjan tarkastuskohdat korvataan tällä listalla. Sen
              jälkeen jokainen uusi hanke, joka alustetaan tästä pohjasta, saa
              nämä tarkastuskohdat. Sama pohja voi olla käytössä useassa
              tehtäväpohjassa, ja muutos koskee niitä kaikkia.
            </p>
            <p>Muutosta ei voi perua tästä ikkunasta.</p>
          </div>
          <p className="text-muted-foreground">
            Jos haluat korjata vain tämän hankkeen tarkastukset, palaa takaisin
            ja poista valinta &quot;Korjaa myös projektipohja&quot;.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setStep("scope")}
            disabled={submitting}
          >
            Takaisin
          </Button>
          <Button onClick={() => void runConfirm()} disabled={submitting}>
            {submitting && <SpinnerIcon className="mr-2 h-4 w-4 animate-spin" />}
            Korjaa myös yrityksen laajuinen tarkastuspohja
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <>
      <div className="py-4 px-1 space-y-3 overflow-y-auto overflow-x-hidden min-h-0 min-w-0">
        {finishedWithoutFrame ? (
          <div className="flex items-start gap-2 rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-700">
            <SuccessIcon className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              {displayProgress?.detail ||
                "Taustatyö on valmistunut. Tulos näkyy taustatöiden listassa."}
            </span>
          </div>
        ) : !displayProgress?.complete ? (
          <>
            <p className="text-sm text-muted-foreground">
              {toFinnishLabel(displayProgress?.stepLabel)}
            </p>
            <Progress value={progressPercent} className="h-1.5" />
            {displayProgress && displayProgress.itemsTotal > 0 && (
              <p className="text-xs text-muted-foreground tabular-nums">
                {displayProgress.itemsDone}/{displayProgress.itemsTotal}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Voit sulkea tämän ikkunan: taustatyö jatkuu, ja sen tulos näkyy
              taustatöiden listassa.
            </p>
          </>
        ) : displayProgress.error ? (
          <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            <AlertIcon className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{displayProgress.error}</span>
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-700">
            <SuccessIcon className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{displayProgress.detail || "Tarkastuskohdat päivitetty."}</span>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button onClick={onDismiss}>Sulje</Button>
      </DialogFooter>
    </>
  );
}
